/**
 * Cuando el lienzo viene corto, la reserva de abajo ya está AFUERA: no se paga
 * dos veces.
 *
 * EL PROBLEMA, MEDIDO
 *
 * El 29-oct-2026, sobre las capturas de Daniel (iPhone 17 Pro Max, 440×956 pt,
 * ArquiFlow instalada como app), pixel por pixel:
 *
 *     pantalla                      956
 *     lienzo que entrega iOS      0..894     (confirmado: el centro de los
 *                                             iconos de la barra cae en 836,
 *                                             que solo cuadra si el lienzo
 *                                             arranca en 0 y muere en 894)
 *     env(safe-area-inset-top)       62
 *     env(safe-area-inset-bottom)    34
 *
 * O sea: el lienzo perdió EXACTAMENTE el recorte de arriba (956 − 62 = 894) y
 * aun así lo dibuja desde el borde 0. Lo causa
 * `apple-mobile-web-app-status-bar-style: black-translucent`, que ya se cambió
 * a `black` en el `index.html` — pero **iOS congela esa meta cuando el icono se
 * agrega a la pantalla de inicio**, así que una app YA INSTALADA sigue con el
 * lienzo corto aunque el servidor entregue la meta nueva.
 *
 * Y abajo del lienzo iOS no dibuja: RECORTA. Estirar el shell esos 62 pt ya se
 * probó (commit 989d8a7) y dejó la barra cortada a 20 pt de sus 82.
 *
 * LO QUE SÍ SE PUEDE HACER
 *
 * Mirar de qué se compone el espacio bajo los iconos de la barra:
 *
 *     con lienzo corto, sin compensar     34 (padding) + 62 (franja muerta) = 96 pt
 *     con lienzo corto, compensado         0 (padding) + 62 (franja muerta) = 62 pt
 *     con el lienzo entero                34 (padding) +  0                 = 34 pt
 *
 * Los 34 pt de `env(safe-area-inset-bottom)` son la reserva para que el
 * indicador de inicio no tape lo que se toca. Si ya sobran 62 pt FUERA del
 * lienzo, esa reserva está servida de más: pagarla otra vez adentro es lo que
 * Daniel ve como banda. Restándola, la barra llega al borde del lienzo y los
 * 62 pt de abajo —que `html` pinta del MISMO `#5D636C` que la barra— se leen
 * como continuación de ella, espejo exacto de la franja de arriba (62 de
 * recorte + 48 de barra arriba; 48 de barra + 62 de recorte abajo).
 *
 * POR QUÉ LAS TRES CONDICIONES, Y NINGUNA DE MENOS
 *
 * 1. `modo app` — en una pestaña de Safari el lienzo también es más corto que
 *    la pantalla, pero por la barra de direcciones, y ahí la reserva de abajo
 *    SÍ hace falta.
 * 2. `recorte de arriba > 0` — ésta es la que distingue los dos lienzos de 894:
 *    con la franja de estado OPACA (lo correcto, y lo que verá una instalación
 *    nueva) el lienzo también mide 894, pero va de 62 a 956 y `env(top)` da 0,
 *    así que su borde de abajo YA ES el de la pantalla y no sobra nada.
 *    Sin esta condición, el arreglo rompería justo el caso bueno.
 * 3. `lo que sobra == el recorte de arriba` — es la firma del defecto, no una
 *    aproximación: el lienzo perdió el recorte de arriba y nada más. Cualquier
 *    otro número (teclado abierto, pestaña con barras, rotación) no califica.
 *
 * Fuera de esas tres, `--sobra-lienzo` queda en 0 y todo se comporta como
 * siempre. En la compu no hace nada.
 */

/** Cuánto mide de VERDAD una expresión de alto en este aparato. */
function medirAlto(expresion: string): number {
  const sonda = document.createElement('div');
  sonda.style.cssText =
    `position:fixed;left:-9999px;top:0;width:1px;height:${expresion}`;
  document.body.appendChild(sonda);
  const alto = sonda.getBoundingClientRect().height;
  sonda.remove();
  return alto;
}

function enModoApp(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true
    || matchMedia('(display-mode: standalone)').matches;
}

/**
 * Mide y escribe `--sobra-lienzo`, que es lo que `--franja-abajo` descuenta.
 * Se vuelve a medir al rotar y al cambiar de tamaño porque los dos cambian el
 * lienzo.
 */
export function compensarLienzoCorto(): void {
  const aplicar = () => {
    const raiz = document.documentElement;
    const sobra = Math.round(screen.height - raiz.clientHeight);
    const recorteArriba = Math.round(medirAlto('var(--franja-arriba, 0px)'));
    const corto = enModoApp()
      && recorteArriba > 2
      && Math.abs(sobra - recorteArriba) <= 2;
    raiz.style.setProperty('--sobra-lienzo', corto ? `${sobra}px` : '0px');
  };

  aplicar();
  addEventListener('resize', aplicar);
  addEventListener('orientationchange', aplicar);
}
